/**
 * SessionTimeoutSelect component
 *
 * Dropdown for selecting session timeout duration.
 * Used in SecuritySettings to allow users to configure auto-logout behavior.
 */
import Select from '@/components/ui/Select';
import type { SessionTimeoutOption } from '@/services/settings';

/**
 * Mapping of timeout option values to human-readable labels
 */
const TIMEOUT_OPTIONS: { value: SessionTimeoutOption; label: string }[] = [
  { value: '15min', label: '15 minutes' },
  { value: '1hr', label: '1 hour' },
  { value: '4hr', label: '4 hours' },
  { value: '24hr', label: '24 hours' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
];

export interface SessionTimeoutSelectProps {
  /** Current selected timeout value */
  value: SessionTimeoutOption;
  /** Callback when selection changes */
  onChange: (value: SessionTimeoutOption) => void;
  /** Whether the select is disabled */
  disabled?: boolean;
}

/**
 * Dropdown component for selecting session timeout duration
 *
 * @example
 * ```tsx
 * <SessionTimeoutSelect
 *   value={sessionPrefs.sessionTimeout}
 *   onChange={handleTimeoutChange}
 *   disabled={isUpdating}
 * />
 * ```
 */
export default function SessionTimeoutSelect({
  value,
  onChange,
  disabled = false,
}: SessionTimeoutSelectProps) {
  return (
    <Select
      options={TIMEOUT_OPTIONS}
      value={value}
      onChange={(e) => onChange(e.target.value as SessionTimeoutOption)}
      disabled={disabled}
      aria-label="Session timeout duration"
      className="w-auto min-w-[140px]"
    />
  );
}
